import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import '@app.scss';
import {
  useMsal,
  useMsalAuthentication,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionType } from '@azure/msal-browser';
import Layout from '@templates/layout/layout';
import Dashboard from '@pages/dashboard/dashboard';
import Forecast from '@pages/forecast/forecast';
import Profile from '@pages/profile/profile';
import NoPageFound from '@pages/noPageFound/noPageFound';
import PastForecast from '@pages/forecast/pastForecast/pastForecast';
import UnAuthorizedPage from '@pages/unAuthorized/unAuthorizedPage';
import { getAccessToken } from '@services/authServices';
import SpinnerLoader from '@atoms/SpinnerLoader';
import LogOutScreen from '@organisms/headerComponent/logOutScreen';
import LayoutAccessRestriction from '@templates/layout/layoutAccessRestriction';
import ForbiddenAccess from '@pages/forbiddenAccess/forbiddenAccess';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  Account_List,
  Current_User,
  Recoil_All_Accounts_Quarter_State,
  Recoil_Quarter_State,
  Country_Name_State,
  Is_User_Available_State,
  System_Date_Time_State,
  Flattened_Account_List_State,
  Recoil_Selected_Account_State,
  Is_Read_Only_State,
  Refresh_Pills_Status_State,
  Refresh_Auth_Token,
} from '@recoil/recoil';
import {
  getCurrentUser,
  getOldCurrentUser,
  logError,
  getUserRoleList,
  sortAccountList,
} from '@util/commonFunctions';
import {
  getAllRoleQuarterlyData,
  getOpportunitiesDue,
  getSystemDateTime,
  getLogOutStatus,
  getUserRole,
  getDashboardProfileDetails,
  checkForSuperUser,
} from '@services/commonServices';
import { ROLE_CONST, RESTART_REQUIRED_SCREEN, SOURCE } from '@constants/commonConstants';
import { getUpdatedCurrentRoleDueDetails } from './util/commonFunctions';
import NoUserFound from '@pages/noUserFound/noUserFound';
import { setNavigateFunction } from '@services/navigationService';
import SuperUserPage from '@pages/superUser/superUserPage';
import { appInsights } from '@services/appInsightsService';

const App = () => {
  const TOKEN_EXPIRATION_WINDOW_MILLIS = 5 * 60 * 1000;
  useMsalAuthentication(InteractionType.Redirect);
  const [userEmailId, setUserEmailId] = useState('');
  const [azureAccessToken, setAzureAccessToken] = useState('');
  const [azureTokenExpiresOn, setAzureTokenExpiresOn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isMslDelegate, setIsMslDelegate] = useState(false);
  const setCountryName = useSetRecoilState(Country_Name_State);
  const [quarterData, setQuarterData] = useRecoilState(Recoil_Quarter_State);
  const [accountList, setAccountList] = useRecoilState(Account_List);
  const [currentUser, setCurrentUser] = useRecoilState(Current_User);
  const [allAccountsQuarterData, setAllAccountsQuarterData] = useRecoilState(
    Recoil_All_Accounts_Quarter_State,
  );
  const [systemDateTime, setSystemDateTime] = useRecoilState(System_Date_Time_State);
  const setIsUserAvailable = useSetRecoilState(Is_User_Available_State);
  const [flattenedAccountList, setflattenedAccountList] = useRecoilState(
    Flattened_Account_List_State,
  );
  const setSelectedUser = useSetRecoilState(Recoil_Selected_Account_State);
  const setIsReadOnlyState = useSetRecoilState(Is_Read_Only_State);
  const refreshPillStatus = useRecoilValue(Refresh_Pills_Status_State);
  const refreshAuthToken = useRecoilValue(Refresh_Auth_Token);
  const [refreshToken, setRefreshToken] = useRecoilState(Refresh_Auth_Token); //eslint-disable-line
  const { instance, inProgress, accounts } = useMsal();

  let currentAssignedUser;
  let arrangedUserRoleDetails;
  let previouslySelectedUser;

  const navigate = useNavigate();

  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  const GetCurrentSystemDateAPI = async () => {
    try {
      if (localStorage.getItem('modifiedSystemDateAndTime')) {
        setSystemDateTime(localStorage.getItem('modifiedSystemDateAndTime'));
        return localStorage.getItem('modifiedSystemDateAndTime');
      } else {
        const dateTime = await getSystemDateTime(localStorage.getItem('userEmail'));
        setSystemDateTime(dateTime?.date);
        return dateTime?.date;
      }
    } catch (error) {
      logError('getSystemDateTime', error);
    }
  };

  useEffect(() => {
    // using settimeout to refresh the token 10 minutes before it expires
    const timeout = setTimeout(
      () => {
        setRefreshToken(true);
      },
      azureTokenExpiresOn - Date.now() - TOKEN_EXPIRATION_WINDOW_MILLIS,
    );
    return () => clearTimeout(timeout);
  }, [azureTokenExpiresOn]);

  useEffect(() => {
    const accessTokenRequest = {
      scopes: ['user.read'],
      account: accounts[0],
      forceRefresh: true,
    };
    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(accessTokenResponse => {
        // Acquire token silent success
        const accessToken = accessTokenResponse.accessToken;
        setAzureAccessToken(accessToken);
        sessionStorage.setItem('azureToken', accessToken);
        const expiryTime = new Date(accessTokenResponse.expiresOn).getTime();
        setAzureTokenExpiresOn(expiryTime);
        sessionStorage.setItem('expiresIn', expiryTime);
      })
      .catch(error => {
        if (error instanceof InteractionRequiredAuthError) {
          instance
            .acquireTokenPopup(accessTokenRequest)
            .then(function (accessTokenResponse) {
              // Acquire token interactive success
              const accessToken = accessTokenResponse.accessToken;
              setAzureAccessToken(accessToken);
              sessionStorage.setItem('azureToken', accessToken);
              const expiryTime = new Date(accessTokenResponse.expiresOn).getTime();
              setAzureTokenExpiresOn(expiryTime);
              sessionStorage.setItem('expiresIn', expiryTime);
            })
            .catch(function (error) {
              // Acquire token interactive failure
              logError('acquireTokenSilent', error);
            });
        }
      })
      .finally(() => {
        setRefreshToken(false);
      });
  }, [instance, accounts, inProgress, refreshAuthToken]);

  useMemo(() => {
    setIsReadOnly(localStorage.getItem('isReadOnly') === 'true' ? true : false);
  }, [localStorage.getItem('isReadOnly')]);

  useMemo(() => {
    setIsMslDelegate(localStorage.getItem('mslDelegate') ? true : false);
  }, [localStorage.getItem('mslDelegate')]);

  useEffect(async () => {
    if (userEmailId == '' && azureAccessToken != '') {
      const userLoginEmail = accounts[0].username;

      setUserEmailId(userLoginEmail);
      localStorage.setItem('userEmail', userLoginEmail?.toLowerCase());
      // Initialize iframe operation flag for super user PL-Opp Name/Comment and GCSP Opp Name
      localStorage.setItem('isIframeOperation', 'false');
      // Initialize MSL new tab operation flag for super user MSL Account Name
      localStorage.setItem('isMslNewTabOperation', 'false');
      // Initialize GCSP hyperlink operation(LENS & CRM) flag for super user
      localStorage.setItem('isGcspHyperlinkOperation', 'false');
      // Initialize MSL hyperlink operation(CRM Power BI) flag for super user
      localStorage.setItem('isMslHyperlinkOperation', 'false');
      appInsights.setAuthenticatedUserContext(userLoginEmail?.toLowerCase());

      await getAccessToken(userLoginEmail?.toLowerCase(), azureAccessToken)
        .then(result => {
          sessionStorage.setItem('authDetails', JSON.stringify(result));
        })
        .catch(error => {
          logError('getAccessToken', error);
          setIsLoading(false);
        });

      // Check for super user
      await checkForSuperUser(userLoginEmail?.toLowerCase())
        .then(result => {
          if (result?.superUserAccess) {
            localStorage.setItem('isSuperUser', true);
            const targetEmail = localStorage.getItem('targetUserEmail');
            localStorage.setItem('targetUserEmail', targetEmail ?? 'null');
          } else {
            localStorage.setItem('isSuperUser', false);
            localStorage.removeItem('targetUserEmail');
          }
        })
        .catch(error => {
          logError('checkForSuperUser', error);
          setIsLoading(!isLoading);
        });

      await getDashboardProfileDetails(userLoginEmail?.toLowerCase())
        .then(result => {
          setCountryName(result?.country);
          fetchUserRole(userLoginEmail?.toLowerCase(), result?.country);
        })
        .catch(error => {
          logError('getDashboardProfileDetails', error);
          setIsLoading(false);
        });
    }
  }, [azureAccessToken, accounts]);

  // User role fetch - check for existing saved user role
  const fetchUserRole = async (userEmail, countryName) => {
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Entering fetchUserRole for ${userEmail} & ${countryName}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });
    const userRoleDetails = await getUserRole(userEmail, countryName);
    const userRoleList = userRoleDetails?.roleList;

    if (!userRoleDetails?.hasRoleAccess) {
      navigate('/access-denied');
      setIsLoading(false); //Added to disable loading spinner

      return;
    }

    if (userRoleList === undefined || userRoleList?.length === 0) {
      navigate('/forbidden-access');
      setIsUserAvailable(false);
      setIsLoading(false); //Added to disable loading spinner
    } else {
      const getSystemDateTime = await GetCurrentSystemDateAPI();
      setIsUserAvailable(true);
      arrangedUserRoleDetails = userRoleList?.sort((a, b) => a.priority - b.priority);
      setAccountList(arrangedUserRoleDetails);
      previouslySelectedUser = JSON.parse(localStorage.getItem('selectedUser'));

      if (userRoleList?.length === 1 && userRoleList[0]?.role === ROLE_CONST.MSL_DELEGATE) {
        localStorage.setItem('mslDelegate', userRoleList?.[0]?.accountList?.[0]?.parent_msl_id);
      }

      let isUserExists;
      if (localStorage.getItem('mslDelegate')) {
        isUserExists = true;
      } else {
        // Handle role mapping when checking if user exists
        const previousRole = previouslySelectedUser?.role;
        isUserExists = userRoleList?.some(roleObj => {
          // Direct role match
          if (roleObj?.role === previousRole) {
            return true;
          }

          // Handle mapped roles: if saved role is 'GCSP' but original is 'GCSP READ ONLY'
          if (previousRole === ROLE_CONST.GCSP && roleObj?.role === ROLE_CONST.GCSP_READ_ONLY) {
            return true;
          }
          if (previousRole === ROLE_CONST.MSL && roleObj?.role === ROLE_CONST.MSL_READ_ONLY) {
            return true;
          }
          if (previousRole === ROLE_CONST.MSL && roleObj?.role === ROLE_CONST.MSL_DELEGATE) {
            return true;
          }

          return false;
        });
      }

      if (localStorage.getItem('selectedUser') && isUserExists) {
        // getOldCurrentUser = Saved Role in local storage
        currentAssignedUser = getOldCurrentUser(previouslySelectedUser);
        setCurrentUser(currentAssignedUser);
      } else {
        if (
          arrangedUserRoleDetails[0]?.role === ROLE_CONST.GCSP_READ_ONLY ||
          arrangedUserRoleDetails[0]?.role === ROLE_CONST.MSL_READ_ONLY ||
          arrangedUserRoleDetails[0]?.role === ROLE_CONST.MSL_DELEGATE
        ) {
          navigate('/forecast');
        }
        currentAssignedUser = getCurrentUser(arrangedUserRoleDetails[0]);
        setCurrentUser(currentAssignedUser);
      }

      await getOpportunitiesDue(
        currentAssignedUser.role,
        currentAssignedUser.email,
        getSystemDateTime.split('T')[0],
      )
        .then(result => {
          setQuarterData(result);
        })
        .catch(error => {
          logError('getOpportunitiesDue', error);
          setIsLoading(false); //Added to disable loading spinner
        });

      // To set all accounts quarter data
      await getAllRoleQuarterlyData(currentAssignedUser.email, getSystemDateTime.split('T')[0])
        .then(result => {
          setAllAccountsQuarterData(result);
        })
        .catch(error => {
          logError('getAllRoleQuarterlyData', error);
          setIsLoading(false);
        });
    }
  };

  // Validate user role with existing saved user
  useEffect(async () => {
    let timeoutId;
    if (
      accountList !== null &&
      currentUser !== null &&
      quarterData !== null &&
      allAccountsQuarterData !== null &&
      systemDateTime !== null
    ) {
      let roleList = await getUserRoleList(
        accountList,
        currentUser,
        allAccountsQuarterData,
        systemDateTime,
      );

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        roleList = roleList.sort(sortAccountList);
        setflattenedAccountList(roleList);

        const previouslySelectedUser = JSON.parse(localStorage.getItem('selectedUser'));

        const isUserExist = roleList.some(
          obj =>
            obj.account_id === previouslySelectedUser?.account_id &&
            obj.read_only === previouslySelectedUser?.read_only,
        );

        if (localStorage.getItem('selectedUser') && isUserExist) {
          setSelectedUser(previouslySelectedUser);
          setIsReadOnlyState(previouslySelectedUser?.read_only);
          localStorage.setItem('isReadOnly', previouslySelectedUser?.read_only);
        } else if (roleList.length > 0) {
          setSelectedUser(roleList[0]);
          setIsReadOnlyState(roleList[0]?.read_only);
          localStorage.setItem('selectedUser', JSON.stringify(roleList[0]));
          localStorage.setItem('isReadOnly', roleList[0]?.read_only);
        } else {
          const modalState = {
            TITLE: RESTART_REQUIRED_SCREEN.HEADER_TITLE,
            DESCRIPTION: RESTART_REQUIRED_SCREEN.DESCRIPTION,
            BUTTON_TITLE: RESTART_REQUIRED_SCREEN.BUTTON_TITLE,
          };
          handleLogout(modalState);
        }

        setIsLoading(false); //Added to disable loading spinner
      }, 3000);
    }
  }, [accountList, currentUser, quarterData, allAccountsQuarterData, systemDateTime]);

  useEffect(async () => {
    if (flattenedAccountList !== null) {
      const updatedFlattenedAccountList = await getUpdatedCurrentRoleDueDetails(
        currentUser,
        quarterData,
        systemDateTime,
        flattenedAccountList,
      );

      setflattenedAccountList(updatedFlattenedAccountList);
    }
  }, [refreshPillStatus]);

  const handleLogout = state => {
    getLogOutStatus(currentUser.email)
      .then(() => {
        localStorage.clear();
        sessionStorage.clear();
        navigate('/logout', {
          state: state,
        });
      })

      .catch(error => {
        logError('getLogOutStatus', error);
      });
  };

  return (
    <>
      {accounts ? (
        <>
          {isLoading ? (
            <div style={{ position: 'fixed', top: '50%', left: '50%' }}>
              <SpinnerLoader color="#155CB4" />
            </div>
          ) : (
            <AuthenticatedTemplate>
              <Routes>
                <Route path="/" element={<Layout />}>
                  {!isReadOnly && !isMslDelegate ? (
                    <>
                      <Route index element={<Dashboard />} />
                      <Route path="forecast" element={<Forecast />} />
                      <Route path="forecast/past" element={<PastForecast />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="*" element={<NoPageFound />} />
                      <Route path="no-user-found" element={<NoUserFound />} />
                    </>
                  ) : (
                    <>
                      <Route index path="forecast" element={<Forecast />} />
                      <Route path="*" element={<NoPageFound />} />
                      <Route path="no-user-found" element={<NoUserFound />} />
                    </>
                  )}
                </Route>

                <Route path="/super-user" element={<SuperUserPage />} />
                <Route path="/forbidden-access" element={<ForbiddenAccess />} />
                <Route path="/access-denied" element={<LayoutAccessRestriction />} />
                <Route path="/logout" element={<LogOutScreen />} />
              </Routes>
            </AuthenticatedTemplate>
          )}
        </>
      ) : (
        <UnauthenticatedTemplate>
          <Routes>
            <Route path="/" element={<UnAuthorizedPage />}>
              <Route path="*" element={<NoPageFound />} />
            </Route>
          </Routes>
        </UnauthenticatedTemplate>
      )}
    </>
  );
};

export default App;
